import { useAuthLogout, useAuthUser } from '@/hooks';
import { ICommonComponentProps } from '@/types';
import { gql } from '@apollo/client';
import { User } from '@auth0/auth0-react';
import { Button, Dropdown, type MenuProps } from 'antd';
import {
  CodeOutlined,
  DownOutlined,
  ExportOutlined,
  LinkOutlined,
  LogoutOutlined,
  MessageOutlined,
  SettingOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHotkeys } from 'react-hotkeys-hook';
import { NewOrgSwitcher } from '@/pages/HomePage/OrganizationPage/Header/UserMenu/NewOrgSwitcher';
import { useSuperUser } from '@/redux/hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemeToggle } from './ThemeToggle';
import { useFeatureGate } from '@statsig/react-bindings';
import { useIsAdmin } from '@/hooks/useAuth';

interface IUserMenuProps extends ICommonComponentProps {
  user: User;
}

export const UserMenu = ({ className, style, ...rest }: IUserMenuProps) => {
  const { selectedClient } = useSuperUser();
  const { name } = useAuthUser();
  const { t } = useTranslation();
  const { isSuperUser } = useAuthUser();
  const navigate = useNavigate();
  const triggerLogout = useAuthLogout();
  const { darkModeEnabled } = useTheme();
  const { value: connectPageEnabled } = useFeatureGate('connect_page');
  const isAdmin = useIsAdmin();
  const [newOrgSwitcherVisible, setNewOrgSwitcherVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const showNewOrgSwitcher = () => setNewOrgSwitcherVisible(true);
  useHotkeys('meta+k', () => {
    datadogRum.addAction('Org. Switcher Shortcut', {
      source: 'settings-and-details',
    });
    showNewOrgSwitcher();
  });

  const handleOpenAccountSettings = useCallback(() => {
    datadogRum.addAction('Open Account Settings', {
      source: 'settings-and-details',
    });
    navigate('/settings/tags');
  }, [navigate]);

  const handleOpenConnect = useCallback(() => {
    datadogRum.addAction('Open Connect', {
      source: 'settings-and-details',
    });
    navigate({ pathname: '/connect/config', search: '' });
  }, [navigate]);
  const displayName = (selectedClient && Object.keys(selectedClient)[0]) || name;

  const items: MenuProps['items'] = useMemo(() => {
    const menuItems: MenuProps['items'] = [];

    if (isSuperUser) {
      menuItems.push({
        key: 'switch-org',
        label: t('switchOrganization'),
        icon: <SwapOutlined />,
        onClick: showNewOrgSwitcher,
        'data-id': 'switchOrganizationButton',
      });
    }

    menuItems.push({
      key: 'account-settings',
      label: 'Account Settings',
      icon: <SettingOutlined />,
      onClick: handleOpenAccountSettings,
      'data-id': 'settings',
    });

    // Admin-only: a shortcut to /connect/config from the user menu.
    // The user menu is the only entry point to Connect now that the
    // top-banner module tabs were pulled.
    if (connectPageEnabled && isAdmin) {
      menuItems.push({
        key: 'connect',
        label: 'Connect',
        icon: <LinkOutlined />,
        onClick: handleOpenConnect,
        'data-id': 'connect',
      });
    }

    menuItems.push({
      key: 'customer-support',
      label: (
        <a
          target="_blank"
          rel="noreferrer"
          href="https://support.butlr.io/hc/en-us/requests/new"
          className="block"
          onClick={() =>
            datadogRum.addAction('Contact Support Clicked', { source: 'settings-and-details' })
          }
        >
          {t('customerSupport')}
        </a>
      ),
      icon: <MessageOutlined />,
      'data-id': 'contactSupportButton',
    });

    menuItems.push({ type: 'divider' });

    menuItems.push({
      key: 'api-doc',
      label: (
        <a
          target="_blank"
          rel="noreferrer"
          href="https://docs.butlr.io"
          className="block"
          onClick={() =>
            datadogRum.addAction('API Docs Clicked', { source: 'settings-and-details' })
          }
        >
          {t('apiDoc')}
        </a>
      ),
      icon: <CodeOutlined />,
    });

    menuItems.push({
      key: 'help-center',
      label: (
        <a
          target="_blank"
          rel="noreferrer"
          href="https://support.butlr.io"
          className="block"
          onClick={() =>
            datadogRum.addAction('Help Center Clicked', { source: 'settings-and-details' })
          }
        >
          {t('helpCenter')}
        </a>
      ),
      icon: <ExportOutlined />,
      'data-id': 'helpCenterButton',
    });

    if (darkModeEnabled) {
      menuItems.push({ type: 'divider' });
      menuItems.push({
        key: 'theme-toggle',
        label: <ThemeToggle />,
        // Keep the dropdown open when interacting with the theme toggle
        onClick: (info) => {
          info.domEvent.stopPropagation();
        },
      });
    }

    menuItems.push({ type: 'divider' });
    menuItems.push({
      key: 'logout',
      label: t('logOut'),
      icon: <LogoutOutlined />,
      danger: true,
      onClick: () => {
        datadogRum.addAction('Logout Triggered', { source: 'user-action' });
        triggerLogout();
      },
      'data-id': 'logoutButton',
    });

    return menuItems;
  }, [
    isSuperUser,
    t,
    handleOpenAccountSettings,
    handleOpenConnect,
    connectPageEnabled,
    isAdmin,
    darkModeEnabled,
    triggerLogout,
  ]);

  return (
    <>
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        placement="bottomRight"
        open={open}
        onOpenChange={setOpen}
        overlayStyle={{ minWidth: 200 }}
      >
        <Button
          data-id="userMenuButton"
          shape="round"
          className={clsx('w-[200px] mr-4 flex items-center justify-between', className)}
          style={{ backgroundColor: 'var(--color-bg-surface)', ...style }}
          {...rest}
        >
          <span className="truncate">{displayName}</span>
          <DownOutlined
            className={`text-xs transition-transform ${open ? 'rotate-180' : 'rotate-0'}`}
          />
        </Button>
      </Dropdown>
      {isSuperUser && (
        <NewOrgSwitcher open={newOrgSwitcherVisible} onOpenChange={setNewOrgSwitcherVisible} />
      )}
    </>
  );
};

UserMenu.fragments = {
  Floor: gql`
    fragment UserMenuFloor on Floor {
      id: floor_id
      timezone
    }
  `,
};
